<template>
  <span>
    <button
      class="executor-icon-btn"
      :disabled="isRunning"
      :title="tooltip"
      @click.stop="handleClick"
    >
      <b-spinner
        v-if="isRunning"
        small
      />
      <i
        v-else
        :class="`fa ${icon}`"
      />
    </button>

    <!-- Floating progress card -->
    <executor-progress-float
      :visible="isRunning && !showModal"
      :title="floatTitle"
      :pct="overallPct"
      :running="isRunning"
      @expand="showModal = true"
      @cancel="cancelRun"
    />

    <!-- Progress modal -->
    <base-modal
      :show="showModal"
      title="Progreso de ejecución"
      :hide-header-close="false"
      :show-cancel="false"
      :show-confirm="false"
      @update:show="showModal = $event"
      @hidden="handleModalHidden"
    >
      <div
        v-if="status === 'pending'"
        class="executor-waiting"
      >
        <b-spinner
          small
          variant="secondary"
          class="mr-2"
        />
        <span class="text-muted">En cola, esperando turno...</span>
      </div>

      <div
        v-else-if="status === 'working' && stages.length === 0"
        class="executor-waiting"
      >
        <b-spinner
          small
          variant="primary"
          class="mr-2"
        />
        <span class="text-muted">Iniciando...</span>
      </div>

      <div
        v-if="stages.length > 0"
        class="executor-overall mb-4"
      >
        <div class="executor-overall-header">
          <span class="executor-overall-label">Progreso general</span>
          <span class="executor-overall-pct">{{ overallPct }}%</span>
        </div>
        <b-progress
          :value="overallPct"
          :max="100"
          :animated="isRunning"
          variant="primary"
          class="executor-overall-bar"
        />
      </div>

      <div class="executor-stages">
        <div
          v-for="stage in stages"
          :key="stage.stage"
          class="executor-stage"
        >
          <div class="stage-row">
            <span class="stage-icon-wrap">
              <b-spinner
                v-if="isStageActive(stage)"
                small
                variant="primary"
              />
              <i
                v-else
                :class="stageIcon(stage)"
              />
            </span>
            <span class="stage-name">{{ stage.stage_name }}</span>
            <span
              v-if="stage.start_ts"
              class="stage-elapsed"
            >
              {{ elapsed(stage) }}
            </span>
          </div>
          <b-progress
            v-if="isStageActive(stage)"
            :value="stage.pct"
            :max="100"
            animated
            variant="primary"
            class="stage-progress"
          />
        </div>
      </div>

      <div
        v-if="status === 'done'"
        class="executor-result executor-result--done mt-3"
      >
        <div class="executor-result__header">
          <i class="fa fa-check-circle mr-2" />
          Completado. La descarga comenzó automáticamente.
        </div>
        <div class="executor-countdown mt-2">
          <b-progress
            :value="countdown"
            :max="10"
            variant="success"
            class="executor-countdown-bar"
          />
          <span class="executor-countdown-text">Cerrando en {{ countdown }}s...</span>
        </div>
      </div>

      <div
        v-if="status === 'error'"
        class="executor-result executor-result--error mt-3"
      >
        <i class="fa fa-exclamation-triangle mr-2" />
        {{ errorMessage }}
      </div>

      <template
        v-if="!isRunning"
        #footer
      >
        <div class="w-100 text-center">
          <b-button
            variant="outline-secondary"
            class="action-btn"
            @click="showModal = false"
          >
            Cerrar
          </b-button>
        </div>
      </template>
      <template
        v-else
        #footer
      >
        <span />
      </template>
    </base-modal>
  </span>
</template>

<script>
import { ref, computed, onBeforeUnmount } from '@vue/composition-api'
import BaseModal from '@/app/shared/components/BaseModal.vue'
import ExecutorProgressFloat from '@/components/ExecutorProgressFloat.vue'
import { createRun, pollUntilDone } from '@/services/executorRunsService'

export default {
  name: 'ExecutorRunButton',
  components: { BaseModal, ExecutorProgressFloat },
  props: {
    executorName: { type: String, required: true },
    requestPayload: { type: Object, required: true },
    icon: { type: String, default: 'fa-download' },
    tooltip: { type: String, default: 'Descargar' }
  },
  setup (props) {
    const status = ref('idle') // idle | pending | working | done | error
    const showModal = ref(false)
    const currentRun = ref(null)
    const errorMessage = ref('')
    const now = ref(Date.now())
    const countdown = ref(0)

    let cancelPoll = null
    let clockTimer = null
    let countdownInterval = null

    const startClock = () => {
      clockTimer = setInterval(() => { now.value = Date.now() }, 1000)
    }

    const stopClock = () => {
      if (clockTimer) { clearInterval(clockTimer); clockTimer = null }
    }

    const isRunning = computed(() =>
      status.value === 'pending' || status.value === 'working'
    )

    const stages = computed(() => {
      const progress = currentRun.value && currentRun.value.progress
      return (progress && progress.stages) || []
    })

    const overallPct = computed(() => {
      const progress = currentRun.value && currentRun.value.progress
      return (progress && progress.overall_pct) || 0
    })

    const floatTitle = computed(() => {
      if (status.value === 'pending') return 'En cola...'
      const active = stages.value.find(s => s.start_ts !== null && s.end_ts === null)
      return active ? active.stage_name : (props.tooltip || 'Procesando...')
    })

    const isStageActive = (stage) =>
      stage.start_ts !== null &&
      stage.end_ts === null &&
      !(currentRun.value && currentRun.value.status === 'ERROR')

    const isStageCompleted = (stage) => stage.end_ts !== null

    const isStageError = (stage) =>
      currentRun.value &&
      currentRun.value.status === 'ERROR' &&
      stage.start_ts !== null &&
      stage.end_ts === null

    const stageIcon = (stage) => {
      if (isStageError(stage)) return 'fa fa-times-circle text-danger'
      if (isStageCompleted(stage)) return 'fa fa-check-circle text-success'
      return 'fa fa-circle-o text-muted'
    }

    const elapsed = (stage) => {
      if (!stage.start_ts) return ''
      const endMs = stage.end_ts
        ? new Date(stage.end_ts).getTime()
        : now.value
      const diffMs = endMs - new Date(stage.start_ts).getTime()
      if (diffMs < 1000) return `${diffMs}ms`
      const diffS = Math.floor(diffMs / 1000)
      if (diffS < 60) return `${diffS}s`
      return `${Math.floor(diffS / 60)}m ${diffS % 60}s`
    }

    const triggerDownload = (run) => {
      if (!run.result || !run.result.download_url) return
      const a = document.createElement('a')
      a.href = run.result.download_url
      a.download = run.result.filename || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }

    const clearCountdown = () => {
      if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null }
    }

    const resetState = () => {
      clearCountdown()
      status.value = 'idle'
      showModal.value = false
      currentRun.value = null
      errorMessage.value = ''
      countdown.value = 0
    }

    const scheduleReset = () => {
      countdown.value = 10
      countdownInterval = setInterval(() => {
        countdown.value -= 1
        if (countdown.value <= 0) resetState()
      }, 1000)
    }

    const cancelRun = () => {
      if (cancelPoll) { cancelPoll(); cancelPoll = null }
      stopClock()
      clearCountdown()
      status.value = 'idle'
      currentRun.value = null
      errorMessage.value = ''
      countdown.value = 0
    }

    const handleModalHidden = () => {
      if (status.value === 'done') resetState()
    }

    const startRun = async () => {
      status.value = 'pending'
      showModal.value = true
      currentRun.value = null
      errorMessage.value = ''

      try {
        const { run_id: runId } = await createRun(props.executorName, props.requestPayload)
        status.value = 'working'
        startClock()

        const { promise, cancel } = pollUntilDone(runId, {
          onProgress: (run) => { currentRun.value = run }
        })
        cancelPoll = cancel

        const finalRun = await promise
        cancelPoll = null
        currentRun.value = finalRun

        if (finalRun.status === 'DONE') {
          status.value = 'done'
          triggerDownload(finalRun)
        } else {
          status.value = 'error'
          errorMessage.value = finalRun.error_message || 'Ocurrió un error inesperado.'
        }
      } catch (err) {
        if (err && err.message === 'cancelled') return
        status.value = 'error'
        errorMessage.value = err && err.message === 'timeout'
          ? 'El proceso superó el tiempo máximo de espera (15 min). Por favor reintenta.'
          : 'Ocurrió un error inesperado. Por favor reintenta.'
      } finally {
        stopClock()
        if (status.value === 'done') scheduleReset()
      }
    }

    const handleClick = () => {
      if (status.value === 'error') {
        status.value = 'idle'
        currentRun.value = null
        errorMessage.value = ''
      }
      if (status.value === 'pending' || status.value === 'working') {
        showModal.value = true
        return
      }
      startRun()
    }

    onBeforeUnmount(() => {
      if (cancelPoll) { cancelPoll(); cancelPoll = null }
      stopClock()
      clearCountdown()
    })

    return {
      status,
      showModal,
      currentRun,
      errorMessage,
      isRunning,
      stages,
      overallPct,
      floatTitle,
      countdown,
      isStageActive,
      stageIcon,
      elapsed,
      handleClick,
      cancelRun,
      handleModalHidden
    }
  }
}
</script>

<style scoped>
.executor-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #a0aec0;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
  flex-shrink: 0;
}

.executor-icon-btn:hover:not(:disabled) {
  color: #4299e1;
  background: rgba(66, 153, 225, 0.1);
}

.executor-icon-btn:disabled {
  cursor: default;
  opacity: 0.6;
}

.action-btn {
  font-weight: 500;
  border-radius: 8px;
}

.executor-waiting {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  color: #718096;
  font-size: 0.9rem;
}

.executor-overall {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
}

.executor-overall-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.executor-overall-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.executor-overall-pct {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4299e1;
}

.executor-overall-bar {
  height: 8px;
  border-radius: 4px;
}

.executor-stages {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.executor-stage {
  padding: 0.75rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.executor-stage:last-child {
  border-bottom: none;
}

.stage-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.stage-icon-wrap {
  width: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stage-name {
  flex: 1;
  font-size: 0.875rem;
  color: #4a5568;
}

.stage-elapsed {
  font-size: 0.8rem;
  color: #718096;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.stage-progress {
  margin-top: 0.5rem;
  height: 4px;
  border-radius: 2px;
}

.executor-result {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
}

.executor-result--done {
  background: #f0fff4;
  color: #276749;
  flex-direction: column;
  align-items: stretch;
}

.executor-result__header {
  display: flex;
  align-items: center;
}

.executor-countdown {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.executor-countdown-bar {
  height: 4px;
  border-radius: 2px;
  transition: width 0.9s linear;
}

.executor-countdown-text {
  font-size: 0.75rem;
  color: #48bb78;
  text-align: right;
}

.executor-result--error {
  background: #fff5f5;
  color: #c53030;
}
</style>



// WEBPACK FOOTER //
// src/app/shared/components/ExecutorRunButton.vue